import React, { useEffect, useRef } from 'react';
import { useAIChat } from '../../contexts/AIChat';
import { useToast } from '../../contexts/Toast';
import useMediaQuery from '../../customHooks/useMediaQuery';
import AIChatMarkdown from './AIChatMarkdown';
import { MOBILE_MEDIA_QUERY, SUGGESTED_PROMPTS } from './constants';

// How close to the bottom (px) still counts as "following" the conversation.
const STICK_TO_BOTTOM_THRESHOLD = 80;

// The clipboard only reliably accepts PNG, so anything else is redrawn through a canvas.
const toPngBlob = async src => {
    const blob = await (await fetch(src)).blob();
    if (blob.type === 'image/png') return blob;

    const image = new Image();
    image.src = src;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth * 2;
    canvas.height = image.naturalHeight * 2;
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return new Promise(resolve => { canvas.toBlob(resolve, 'image/png'); });
};

// Charts from earlier sessions are links to storage on another origin, where the
// `download` attribute is ignored, so the file is fetched and saved from a blob URL.
// If that's not allowed either, the chart opens in a new tab instead.
const downloadImage = async image => {
    const link = document.createElement('a');
    link.download = image.fileName || 'zelp-ai-chart';
    let objectUrl = null;
    try {
        if (!image.src.startsWith('data:')) {
            objectUrl = URL.createObjectURL(await (await fetch(image.src)).blob());
        }
        link.href = objectUrl || image.src;
    }
    catch (e) {
        link.href = image.src;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
    }
    link.click();
    if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const ChatImage = ({ image, onLoad }) => {
    const { successToast, errorToast } = useToast();

    const copyImage = async () => {
        try {
            const blob = await toPngBlob(image.src);
            // eslint-disable-next-line no-undef
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            successToast('Chart copied to clipboard.');
        }
        catch (e) {
            errorToast('Couldn\'t copy the chart. Try downloading it instead.');
        }
    };

    return (
        <figure className="ai-chat_image-card">
            <div className="ai-chat_image-card_header">
                <figcaption className="ai-chat_image-card_title">{image.title}</figcaption>
                <div className="ai-chat_image-card_actions">
                    <button
                        type="button"
                        className="ai-chat_card-action"
                        aria-label="Copy chart"
                        title="Copy"
                        onClick={copyImage}>
                        <i className="ai-chat_copy-icon" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="ai-chat_card-action"
                        aria-label="Download chart"
                        title="Download"
                        onClick={() => downloadImage(image)}>
                        <i className="zelpicon zelpicon-download" aria-hidden="true" />
                    </button>
                </div>
            </div>
            <img
                className="ai-chat_image-card_image"
                src={image.src}
                alt={image.title || 'Chart'}
                onLoad={onLoad} />
        </figure>
    );
};

const TypingIndicator = ({ label = 'Insight AI is responding' }) => (
    <div className="ai-chat_bubble ai-chat_typing" role="status" aria-label={label}>
        <span />
        <span />
        <span />
    </div>
);

const EmptyState = ({ onSuggestionClick }) => (
    <div className="ai-chat_empty">
        <div className="ai-chat_empty_icon">
            <i className="zelp-ai-icon zelp-ai-icon__mono" aria-hidden="true" />
        </div>
        <h3 className="ai-chat_empty_title">How can I help with your farm?</h3>
        <p className="ai-chat_empty_text">
            Ask about your animals, devices, emissions or projects. You can attach a file to ask about it too.
        </p>
        <ul className="ai-chat_suggestions" aria-label="Suggested questions">
            {SUGGESTED_PROMPTS.map(prompt => (
                <li key={prompt}>
                    <button type="button" className="ai-chat_suggestion" onClick={() => onSuggestionClick(prompt)}>
                        {prompt}
                    </button>
                </li>
            ))}
        </ul>
    </div>
);

const Message = ({ message, onImageLoad, onHelpLinkClick }) => {
    const isAssistant = message.role === 'assistant';
    const isStreaming = message.status === 'streaming';
    const hasContent = !!message.text || !!message.images?.length;

    // The answer can link to a chart by the file name it had in the model's sandbox
    // (`sandbox:/mnt/data/chart.png`); that link downloads the chart with that name,
    // or the only chart when there's just one.
    const resolveFileLink = url => {
        const images = message.images || [];
        const name = url.split('/').pop();
        const image = images.find(item => item.fileName === name) || (images.length === 1 ? images[0] : null);
        return image ? () => downloadImage(image) : null;
    };

    return (
        <div className={`ai-chat_message ai-chat_message__${message.role}`}>
            {message.attachments?.length ? (
                <ul className="ai-chat_attachments" aria-label="Attached files">
                    {message.attachments.map(file => (
                        <li key={file.id} className="ai-chat_attachment">
                            <i className="zelpicon zelpicon-attachment" aria-hidden="true" />
                            <span>{file.name}</span>
                        </li>
                    ))}
                </ul>
            ) : ''}
            {message.text ? (
                <div className={`ai-chat_bubble ${isAssistant ? 'ai-chat_bubble__markdown' : ''}`}>
                    {isAssistant
                        ? (
                            <AIChatMarkdown
                                text={message.text}
                                resolveFileLink={resolveFileLink}
                                onImageLoad={onImageLoad}
                                onHelpLinkClick={onHelpLinkClick} />
                        )
                        : message.text}
                </div>
            ) : ''}
            {message.images?.map(image => <ChatImage key={image.id} image={image} onLoad={onImageLoad} />)}
            {/* charts can take a minute after the text, so the indicator stays until the answer ends */}
            {isStreaming && <TypingIndicator />}
            {message.status === 'stopped' && (
                <p className="ai-chat_status-note">{hasContent ? 'Stopped' : 'Stopped before Insight AI answered'}</p>
            )}
            {message.status === 'error' && (
                <p className="ai-chat_error" role="alert">
                    <i className="zelpicon zelpicon-warning" aria-hidden="true" />
                    {message.error}
                </p>
            )}
        </div>
    );
};

const AIChatMessages = () => {
    const {
        messages, isResponding, sendMessage, historyStatus, retryHistory, closeChat
    } = useAIChat();
    // On mobile the drawer covers the page, so it closes to show the help article a link opens.
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
    const listRef = useRef(null);
    // follows new content only while the user is at the bottom, so reading
    // further up isn't interrupted by every streamed word
    const stickToBottomRef = useRef(true);

    const onScroll = () => {
        const list = listRef.current;
        if (!list) return;
        stickToBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < STICK_TO_BOTTOM_THRESHOLD;
    };

    const scrollToBottom = () => {
        const list = listRef.current;
        if (list && stickToBottomRef.current) list.scrollTo({ top: list.scrollHeight });
    };

    // Images load after the message renders and grow the list, so they scroll again on load.
    useEffect(scrollToBottom, [messages]);

    // a question the user sends always brings them back to the bottom
    useEffect(() => {
        if (isResponding) {
            stickToBottomRef.current = true;
            scrollToBottom();
        }
    }, [isResponding]);

    // 'idle' too: the load starts in an effect, so the empty state would flash first
    const isLoadingHistory = (historyStatus === 'idle' || historyStatus === 'loading') && !messages.length;

    return (
        <div
            className="ai-chat_messages"
            ref={listRef}
            role="log"
            aria-live="polite"
            aria-label="Conversation"
            aria-busy={isLoadingHistory}
            onScroll={onScroll}>
            {historyStatus === 'error' && (
                <div className="ai-chat_history-error" role="alert">
                    <span>Couldn&apos;t load your earlier messages.</span>
                    <button type="button" className="ai-chat_link-button" onClick={retryHistory}>Try again</button>
                </div>
            )}

            {isLoadingHistory && (
                <div className="ai-chat_message ai-chat_message__assistant">
                    <TypingIndicator label="Loading your conversation" />
                </div>
            )}

            {!messages.length && !isLoadingHistory ? (
                <EmptyState onSuggestionClick={text => sendMessage({ text })} />
            ) : messages.map(message => (
                <Message
                    key={message.id}
                    message={message}
                    onImageLoad={scrollToBottom}
                    onHelpLinkClick={isMobile ? closeChat : undefined} />
            ))}
        </div>
    );
};

export default AIChatMessages;
